import { useState, useRef } from "react";
import fetchWithCredentials from "apiClient";
import { useStudents } from "context/StudentsContext";
import { useAuth } from "context/AuthContext";
import { Dialog } from "./ui/Dialog";

interface StudentMenuProps {
  studentId: number;
  status: "active" | "inactive";
  onStatusChange: () => void;
}

export function StudentMenu({
  studentId,
  status,
  onStatusChange,
}: StudentMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { refresh, activeStudentsCount } = useStudents();
  const { planLimit } = useAuth();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleToggleStatus = async () => {
    try {
      await fetchWithCredentials(`/api/teacher/students/${studentId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: status === "active" ? "inactive" : "active",
        }),
      });

      await refresh();
      onStatusChange();
      setIsOpen(false);
    } catch (error) {
      console.error("Error updating student status:", error);
    }
  };

  const isActivateDisabled =
    status === "inactive" && activeStudentsCount >= planLimit;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 hover:bg-gray-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <svg
          className="w-4 h-4 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
          />
        </svg>
      </button>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
        position="right"
        className="p-0"
      >
        <button
          onClick={handleToggleStatus}
          disabled={isActivateDisabled}
          className={`block w-full text-left px-4 py-2 text-sm ${
            isActivateDisabled
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-100"
          }`}
          title={
            isActivateDisabled
              ? "Upgrade your plan to activate more students"
              : undefined
          }
        >
          {status === "active" ? "Deactivate" : "Activate"}
        </button>
      </Dialog>
    </div>
  );
}
